import {
  ActionReducerMapBuilder,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit'
import { isArray } from 'lodash'
import { ProjectState } from 'store/Project/ProjectTypes'
import { addRegressionTest } from './asyncActions/addRegressionTest'
import { applyWorkingStepToAllParentFilteredRows } from './asyncActions/applyWorkingStepToAllParentFilteredRows'
import { batchUpdateBomItems } from './asyncActions/batchUpdateBomItems'
import { cancelOrder } from './asyncActions/cancelOrder'
import { checkManufacturability } from './asyncActions/checkManufacturability'
import { CostDriversActions } from './asyncActions/CostDriversActions'
import { deleteBomItemAttachment } from './asyncActions/deleteAttachment'
import { deleteBoMItem } from './asyncActions/deleteBoMItem'
import { downloadAttachment } from './asyncActions/downloadAttachment'
import { downloadBomItemFiles } from './asyncActions/downloadBomItemFiles'
import { fetchBomItem } from './asyncActions/fetchBomItem'
import { fetchProject } from './asyncActions/fetchProject'
import { recalculateProject } from './asyncActions/recalculateProject'
import { recalculateProjectWithUpdatedPrices } from './asyncActions/recalculateProjectWithUpdatedPrices'
import {
  forgetAsPurchasingPart,
  rememberAsPurchasingPart,
} from './asyncActions/rememberAsPurchasingPart'
import { saveBomItemIsPurchasingPart } from './asyncActions/saveBomItemIsPurchasingPart'
import { saveBomItemMaterialCostPrice } from './asyncActions/saveBomItemMaterialCostPrice'
import { saveBomItemMaterials } from './asyncActions/saveBomItemMaterials'
import { saveBomItemName } from './asyncActions/saveBomItemName'
import { saveBomItemQuantity } from './asyncActions/saveBomItemQuantity'
import { saveBomItemResource } from './asyncActions/saveBomItemResource'
import { saveBomItemTargetPrice } from './asyncActions/saveBomItemTargetPrice'
import { saveBomItemComment } from './asyncActions/saveComment'
import { saveNestingDirection } from './asyncActions/saveNestingDirection'
import { saveOrderNumber } from './asyncActions/saveOrderNumber'
import { saveProjectBatchSize } from './asyncActions/saveProjectBatchSize'
import { saveProjectReference } from './asyncActions/saveProjectReference'
import { saveProjectStatus } from './asyncActions/saveProjectStatus'
import { toggleManufacturabilityFlag } from './asyncActions/toggleManufacturabilityFlag'
import { uploadAttachments } from './asyncActions/uploadAttachments'
import { uploadGeometry } from './asyncActions/uploadGeometry'
import { getActionPrefix } from './newProjectReducer'
import { SolveIssueAsync } from './asyncActions/SolveIssueAsync'
import { saveProjectRequestedDeliveryDate } from './asyncActions/saveProjectRequestedDeliveryDate'
import { downloadProjectDeliveryDateEstimation } from './asyncActions/downloadProjectDeliveryDateEstimation'

export function addAsyncOperationStatus(
  builder: ActionReducerMapBuilder<ProjectState>
) {
  builder.addMatcher(
    isPending(
      uploadGeometry,
      uploadAttachments,
      saveOrderNumber,
      saveProjectReference,
      saveProjectStatus,
      cancelOrder,
      saveProjectBatchSize,
      saveBomItemQuantity,
      saveBomItemName,
      saveBomItemMaterials,
      saveBomItemIsPurchasingPart,
      saveBomItemResource,
      saveBomItemMaterialCostPrice,
      fetchProject,
      fetchBomItem,
      deleteBoMItem,
      downloadBomItemFiles,
      addRegressionTest,
      checkManufacturability,
      rememberAsPurchasingPart,
      forgetAsPurchasingPart,
      deleteBomItemAttachment,
      downloadAttachment,
      saveNestingDirection,
      saveBomItemTargetPrice,
      saveBomItemComment,
      toggleManufacturabilityFlag,
      recalculateProject,
      recalculateProjectWithUpdatedPrices,
      applyWorkingStepToAllParentFilteredRows,
      CostDriversActions.ResetCostDriverDiscountToDefault,
      CostDriversActions.ResetCostDriverSurchargeToDefault,
      CostDriversActions.SaveCostDriverDiscountRatio,
      CostDriversActions.SaveCostDriverSurchargeRatio,
      batchUpdateBomItems,
      SolveIssueAsync,
      saveProjectRequestedDeliveryDate,
      downloadProjectDeliveryDateEstimation
    ),
    recordRequestInfo
  )
  builder.addMatcher(
    isFulfilled(
      uploadGeometry,
      uploadAttachments,
      saveOrderNumber,
      saveProjectReference,
      saveProjectStatus,
      cancelOrder,
      saveProjectBatchSize,
      saveBomItemQuantity,
      saveBomItemName,
      saveBomItemMaterials,
      saveBomItemIsPurchasingPart,
      saveBomItemResource,
      saveBomItemMaterialCostPrice,
      fetchProject,
      fetchBomItem,
      deleteBoMItem,
      downloadBomItemFiles,
      addRegressionTest,
      checkManufacturability,
      rememberAsPurchasingPart,
      forgetAsPurchasingPart,
      deleteBomItemAttachment,
      downloadAttachment,
      saveNestingDirection,
      saveBomItemTargetPrice,
      saveBomItemComment,
      toggleManufacturabilityFlag,
      recalculateProject,
      recalculateProjectWithUpdatedPrices,
      applyWorkingStepToAllParentFilteredRows,
      CostDriversActions.ResetCostDriverDiscountToDefault,
      CostDriversActions.ResetCostDriverSurchargeToDefault,
      CostDriversActions.SaveCostDriverDiscountRatio,
      CostDriversActions.SaveCostDriverSurchargeRatio,
      batchUpdateBomItems,
      SolveIssueAsync,
      saveProjectRequestedDeliveryDate,
      downloadProjectDeliveryDateEstimation
    ),
    recordRequestInfo
    // (state, action) => {
    //   delete state.currentOperations[
    //     getActionPrefix(
    //       action,
    //       action.meta.arg ? action.meta.arg['bomItemId'] : undefined
    //     )
    //   ]
    // }
  )
  builder.addMatcher(
    isRejected(
      uploadGeometry,
      uploadAttachments,
      saveOrderNumber,
      saveProjectReference,
      saveProjectStatus,
      cancelOrder,
      saveProjectBatchSize,
      saveBomItemQuantity,
      saveBomItemName,
      saveBomItemMaterials,
      saveBomItemIsPurchasingPart,
      saveBomItemResource,
      saveBomItemMaterialCostPrice,
      fetchProject,
      fetchBomItem,
      deleteBoMItem,
      downloadBomItemFiles,
      addRegressionTest,
      checkManufacturability,
      rememberAsPurchasingPart,
      forgetAsPurchasingPart,
      deleteBomItemAttachment,
      downloadAttachment,
      saveNestingDirection,
      saveBomItemTargetPrice,
      saveBomItemComment,
      toggleManufacturabilityFlag,
      recalculateProject,
      recalculateProjectWithUpdatedPrices,
      applyWorkingStepToAllParentFilteredRows,
      CostDriversActions.ResetCostDriverDiscountToDefault,
      CostDriversActions.ResetCostDriverSurchargeToDefault,
      CostDriversActions.SaveCostDriverDiscountRatio,
      CostDriversActions.SaveCostDriverSurchargeRatio,
      batchUpdateBomItems,
      SolveIssueAsync,
      saveProjectRequestedDeliveryDate,
      downloadProjectDeliveryDateEstimation
    ),
    recordRequestError
  )

  function recordRequestError(state, action) {
    if (isArray(action.meta.arg) && action.meta.arg[0].file) {
      delete action.meta.arg
    }

    state.currentOperations[
      getActionPrefix(
        action,
        action.meta.arg ? action.meta.arg['bomItemId'] : undefined
      )
    ] = {
      ...action.meta,
      error: action.error,
    }
  }

  function recordRequestInfo(state, action) {
    if (isArray(action.meta.arg) && action.meta.arg[0].file) {
      delete action.meta.arg
    }

    state.currentOperations[
      getActionPrefix(
        action,
        action.meta.arg ? action.meta.arg['bomItemId'] : undefined
      )
    ] = { ...action.meta }
  }
}
